import * as Sentry from '@sentry/react-native'
import { getDefaultStore } from 'jotai'
import { clientsApi } from '../global/API/typedApiClient'
import { schemas, type ClientResponse } from '@atlas/api-client'
import type { z } from 'zod'
import { localRowsOverlayAtom, type PendingRow } from '../global/Write/localViewState'
import { idsOfRows } from '../global/Write/localViewOverlay'
import { observeLocalViewRows } from '../global/Write/localView'

/**
 * ATL-1445 stage 3 P6 (plan §9 risk 5) — a client created on this device whose master write has not
 * reached the server yet is not in `GET /clients/search`. Without this it could not be attached to a
 * sale until its write was delivered. The search's own result is returned AS IS when this device has
 * no pending clients (every session today: same array object); otherwise pending clients whose name or
 * phone contains the query are appended, de-duplicated by id — a client the server returned wins.
 *
 * The match is a plain case-insensitive substring of `name` and of `phone`'s digits, a deliberate
 * subset of the backend's search: a pending client is found by what the cashier just typed into it.
 * Each match is validated against the generated client schema (minus `supabaseId`) and returned as a
 * `PendingClient` — never cast into a `ClientResponse` it is not.
 * A FAILED search stays a failure (ATL-742): the pending matches are not presented as "the" result of
 * a search that did not run (an open question in the PR).
 */
function withPendingClients(query: string, clients: ClientResponse[]): SearchedClient[] {
  const rows = getDefaultStore().get(localRowsOverlayAtom)
  const pending: readonly PendingRow[] = rows ? rows.clients : []
  if (pending.length === 0) return clients
  const serverIds = idsOfRows(clients as Array<{ id?: unknown; firestoreId?: unknown }>)
  observeLocalViewRows('clients', serverIds)
  const needle = query.toLowerCase()
  const digits = query.replace(/\D/g, '')
  const matches = pending.filter((row) => {
    if (serverIds.has(row.id)) return false
    const name = typeof row.fields.name === 'string' ? row.fields.name.toLowerCase() : ''
    const phone = typeof row.fields.phone === 'string' ? row.fields.phone.replace(/\D/g, '') : ''
    return name.includes(needle) || (digits.length > 0 && phone.includes(digits))
  })
  const pendingClients: PendingClient[] = []
  for (const row of matches) {
    const parsed = PendingClientSchema.safeParse({ ...row.fields, id: row.id, firestoreId: row.id })
    if (parsed.success) {
      pendingClients.push({ ...parsed.data, supabaseId: null, pendingLocalWrite: true })
    } else if (!reportedInvalidPending.has(row.id)) {
      // A record P13's writer built in a shape the response schema refuses: not shown, said once.
      reportedInvalidPending.add(row.id)
      console.warn('[clientSearch] a pending client record does not match the client schema; it is not offered', { id: row.id, issues: parsed.error.issues.length })
    }
  }
  if (pendingClients.length === 0) return clients
  return [...clients, ...pendingClients]
}

/**
 * Result of a client search, with failure kept DISTINCT from "no matches".
 *
 * Both search call sites (the New Sale client-attach box and the Clients screen)
 * previously did `catch { setHits([]) }`, which rendered a network failure, a 401
 * and a Zod response rejection identically to a genuine empty result. The cashier
 * was told "клиент не найден" for a client that exists, and — because the catch
 * only did `console.error` — the failure produced NO telemetry at all, so it was
 * invisible in Sentry (ATL-742). Callers must branch on `status`, never treat an
 * error as an empty list.
 */
export type ClientSearchResult =
  | { status: 'ok'; clients: SearchedClient[] }
  | { status: 'error'; error: unknown }

/**
 * ATL-1445 stage 3 P6: a client this device created whose master write the server has not acknowledged
 * yet. It has every field of a `ClientResponse` (validated against the generated schema) except a
 * Supabase id, which does not exist until the server has the row — so it says so (`supabaseId: null`)
 * instead of inventing one.
 */
/** The generated response schema minus the id only the server can mint. */
const PendingClientSchema = schemas.ClientFirestoreResponse.omit({ supabaseId: true })

// From the schema, not `Omit<ClientResponse, …>`: the response type is `.passthrough()` (an index
// signature), and `Omit` over an index signature drops every named field.
export type PendingClient = z.infer<typeof PendingClientSchema> & { supabaseId: null; pendingLocalWrite: true }
export type SearchedClient = ClientResponse | PendingClient
const reportedInvalidPending = new Set<string>()

/** Below this length the backend returns [] by design, so we never call it. */
const MIN_QUERY_LENGTH = 2

type ApiErrorShape = {
  message?: string
  code?: string
  status?: number
  response?: { status?: number }
}

/**
 * Search clients via the backend (Supabase-backed since ATL-680), reporting any
 * failure to Sentry and returning it to the caller instead of swallowing it.
 *
 * The raw query is deliberately NOT sent to Sentry: it is an end customer's name
 * or phone number. Length and shape are enough to reproduce a failing call
 * without putting client PII into an error tracker.
 */
export async function searchClientsWithTelemetry(
  query: string,
  limit?: number,
  context: { screen: string; organizationId?: string; storeId?: string } = { screen: 'unknown' }
): Promise<ClientSearchResult> {
  const trimmed = (query ?? '').trim()
  if (trimmed.length < MIN_QUERY_LENGTH) {
    return { status: 'ok', clients: [] }
  }

  try {
    const clients = await clientsApi.searchClients(trimmed, limit)
    return { status: 'ok', clients: withPendingClients(trimmed, clients) }
  } catch (error) {
    const apiError = (error ?? {}) as ApiErrorShape
    console.error('Client search error:', error)
    Sentry.captureException(error, {
      tags: {
        client_search_error: true,
        screen: context.screen,
      },
      extra: {
        endpoint: '/clients/search',
        // Query omitted on purpose (end-customer PII) — shape only.
        queryLength: trimmed.length,
        queryIsNumeric: /^\d+$/.test(trimmed),
        limit,
        organizationId: context.organizationId,
        storeId: context.storeId,
        errorName: (error as Error)?.name,
        errorMessage: apiError.message,
        errorCode: apiError.code,
        statusCode: apiError.status ?? apiError.response?.status,
      },
    })
    return { status: 'error', error }
  }
}
